import { type ReactNode, createContext, useContext } from 'react'
import { getIsSeries } from 'libs/utils'
import type { CourseTile } from '../types'
import type { ClassTileProps } from './types'

type ClassTileContextValue = {
  tile: CourseTile
  isPrioritized: boolean
  isSeries: boolean
  clickBehavior: 'watchTrailer' | 'navigate'
  carouselSlug: string
  location: string
  onClick: ClassTileProps['onClick']
  onClickWatchTrailer?: () => void
  showWatchTrailerButton: boolean
  trackProps?: Record<string, string | number | undefined>
}

const ClassTileContextStore = createContext<ClassTileContextValue | null>(null)

export const useClassTile = () => {
  const context = useContext(ClassTileContextStore)

  if (!context) {
    throw new Error('useClassTile must be used within ClassTileContext')
  }

  return context
}

type ClassTileContextProps = {
  children: ReactNode
  carouselSlug: string
  location: string
  onClick: ClassTileProps['onClick']
  onClickWatchTrailer?: () => void
} & Omit<ClassTileContextValue, 'isSeries' | 'showWatchTrailerButton'>

export const ClassTileContext = ({
  children,
  carouselSlug,
  location,
  onClick,
  onClickWatchTrailer,
  tile,
  isPrioritized,
  clickBehavior,
  trackProps,
}: ClassTileContextProps) => {
  const isSeries = getIsSeries(tile.type)

  const showWatchTrailerButton =
    clickBehavior === 'watchTrailer' && Boolean(tile?.mediaUuid)

  const contextValue = {
    tile,
    isPrioritized,
    isSeries,
    clickBehavior,
    carouselSlug,
    location,
    onClick,
    onClickWatchTrailer,
    showWatchTrailerButton,
    trackProps,
  }
  return (
    <ClassTileContextStore.Provider value={contextValue}>
      {children}
    </ClassTileContextStore.Provider>
  )
}
